import * as moment from 'moment'

import { LANGUAGES } from '../../../@core/utils/constants'
import { TranslationDto } from '../translation.dto'

import { ChallengeStatus, ChallengeTimePhase } from './challenge.dto'

/**
 * Adapter giữa 2 cơ chế i18n:
 *
 *   BE (Stamp Collection)          FE (toàn bộ template hiện có)
 *   ----------------------------   -----------------------------
 *   request : translations[]        TranslationDto { en, kh }
 *   response: translations[]  (API #10, #11, #12 — detail/create/update)
 *   response: string localize (API #9, #17-22 — theo header Accept-Language)
 *
 * Toàn bộ template Stamp Collection đọc `field?.en`, nên lớp service **luôn** trả
 * `TranslationDto` — hai hàm dưới đây là chỗ duy nhất biết BE dùng dạng nào.
 *
 * ✅ ĐÃ CHỐT: API #9 (list) trả string đã localize, API #10 (detail) trả `translations[]`
 * — là chủ ý hợp lý (list để hiển thị nhanh, detail để có đủ dữ liệu edit cả 2 ngôn ngữ),
 * đúng với contract hiện tại. Nếu sau này BE đổi list sang `translations[]` thì chỉ cần đổi
 * `localizedToTranslation` → `toTranslation` tại `AdminChallengeService.list()`.
 */

/** Một phần tử `translations[]` của BE. */
export interface ApiTranslationDto {
  languageCode?: string
  name?: string
  description?: string
}

/** BE nhận đúng 2 language code (API Design: `"en|kh"`). */
export const API_LANGUAGE_CODES: string[] = [LANGUAGES.EN, LANGUAGES.KH]

function pick(translations: ApiTranslationDto[], languageCode: string): ApiTranslationDto {
  return (translations || []).find(item => item?.languageCode === languageCode) || {}
}

/** `translations[] → TranslationDto` cho một khoá (`name` hoặc `description`). */
export function toTranslation(
  translations: ApiTranslationDto[],
  key: keyof ApiTranslationDto = 'name'
): TranslationDto {
  return TranslationDto.create(
    pick(translations, LANGUAGES.EN)[key],
    pick(translations, LANGUAGES.KH)[key]
  )
}

/**
 * Response đã localize (string) → `TranslationDto`.
 *
 * Điền cùng giá trị vào cả `en` và `kh` để template `field?.en` chạy đúng mà không
 * phải sửa. Giá trị thực tế là ngôn ngữ của header `Accept-Language`, **không phải**
 * bản tiếng Anh — chỉ dùng để hiển thị, tuyệt đối không dùng làm dữ liệu Save.
 */
export function localizedToTranslation(value?: string): TranslationDto {
  return TranslationDto.create(value, value)
}

/** `TranslationDto → translations[]` cho request Create/Update (API #11, #12). */
export function toApiTranslations(
  name?: TranslationDto,
  description?: TranslationDto
): ApiTranslationDto[] {
  const build = (
    languageCode: string,
    nameValue?: string,
    descriptionValue?: string
  ): ApiTranslationDto => {
    const item: ApiTranslationDto = { languageCode, name: nameValue || '' }

    // Không truyền `description` thì bỏ hẳn khoá thay vì gửi chuỗi rỗng.
    if (description) {
      item.description = descriptionValue || ''
    }

    return item
  }

  return [
    build(LANGUAGES.EN, name?.en, description?.en),
    build(LANGUAGES.KH, name?.kh, description?.kh)
  ]
}

/**
 * `<input type="datetime-local">` → datetime ISO 8601 có offset UTC+7 (`+07:00`) cho BE.
 *
 * `[(ngModel)]` của `datetime-local` giữ chuỗi dạng `"2026-11-10T09:00"` (theo giờ UTC+7,
 * khớp với `toDateTimeLocal()`). Giữ đúng thời điểm khi đọc rồi gửi lại (`2026-11-10T09:00:00+07:00`).
 * Implements: FEAT-001#AC-008, AC-008
 * Tests: src/app/+governance-admin/+stamp-collection/edit-challenge/reward-builder/reward-option-form/reward-option-form.component.spec.ts
 */
export function toApiDateTime(value?: string): string | null {
  if (!value) {
    return null
  }

  const trimmed = value.trim()
  const hasExplicitTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)

  if (hasExplicitTimezone) {
    const parsedWithTz = moment.parseZone(trimmed)
    return parsedWithTz.isValid()
      ? parsedWithTz.utcOffset('+07:00').format('YYYY-MM-DDTHH:mm:ssZ')
      : null
  }

  const withSeconds = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed)
    ? `${trimmed}:00`
    : trimmed
  const parsed = moment.parseZone(`${withSeconds}+07:00`)

  return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm:ssZ') : null
}

/**
 * Bắc cầu `ChallengeTimePhase` (BE thật, 4 giá trị) → `ChallengeStatus` (3 giá trị cũ).
 *
 * ✅ ĐÃ CHỐT: UI hiển thị chính (chip trên card, khoá form Edit) dùng thẳng `timePhase` +
 * `lifecycleStatus` thật — xem `ChallengeItemComponent`, `EditChallengeComponent.contentOnly`.
 * Hàm này chỉ còn 2 việc: (1) fallback hiển thị cho API #9 (List) khi response chưa xác nhận
 * có trả `timePhase` hay không; (2) input cho `canDeleteChallenge()` — chỉ cần phân biệt
 * đúng `PLANNED`, ý nghĩa không đổi giữa 2 model nên bắc cầu không làm sai kết quả.
 * `REDEMPTION_ONLY` bắc cầu thành `ONGOING` (challenge vẫn "sống", chỉ hết giai đoạn phát tem).
 */
export function toBridgedStatus(timePhase?: ChallengeTimePhase): ChallengeStatus {
  if (timePhase === ChallengeTimePhase.PLANNED) {
    return ChallengeStatus.PLANNED
  }

  if (timePhase === ChallengeTimePhase.ENDED) {
    return ChallengeStatus.ENDED
  }

  // ONGOING hoặc REDEMPTION_ONLY — cả 2 tạm hiện chung nhãn "Ongoing"
  return ChallengeStatus.ONGOING
}
